// Accès à l'API. Une seule fonction : le reste est typé au point d'appel.

export type Question = {
  ref: string;
  label: string;
  poids: number;
  bonus: boolean;
  exercice: number;
  titre_exercice: string;
  competences: string[];
};

export type Copie = {
  copie_id: number;
  prenom: string;
  nom: string;
  statut: "presente" | "absente" | "non_rendue";
  notes: Record<string, number>;
};

export type Grille = {
  ds: { id: number; titre: string; classe: string; total_points: number };
  questions: Question[];
  copies: Copie[];
};

export type ResumeDs = {
  id: number;
  titre: string;
  classe: string;
  nom_fichier: string;
  questions: number;
  copies: number;
  copies_presentes: number;
  notes_saisies: number;
  notes_attendues: number;
  competences: number;
};

// --- QCM -------------------------------------------------------------------
// Une famille d'objets parallèle à celle des devoirs, sans clé commune :
// `/api/ds/3` et `/api/qcm/3` ne désignent pas le même objet.

export type ItemQcm = {
  id: number;
  numero: number;
  enonce: string;   // maths en syntaxe Typst
  reponses: number; // nombre de réponses proposées : de 2 à 6
  poids: number;
};

/** Ce que le professeur a relevé : la place cochée, ou une anomalie. */
export type ReponseSaisie = number | "blanche" | "illisible";

export type CopieQcm = {
  copie_id: number;
  prenom: string;
  nom: string;
  statut: "presente" | "absente" | "non_rendue";
  // Lettre du sujet qu'avait cet élève. Tant qu'elle manque, ses réponses ne
  // veulent rien dire : c'est elle qui dit dans quel ordre elles étaient
  // imprimées.
  version: string | null;
  reponses: Record<string, ReponseSaisie>;
};

export type GrilleQcm = {
  qcm: {
    id: number;
    titre: string;
    classe: string;
    versions: string[];
    total_points: number;
    // Par version puis par question, la place où la bonne réponse a été
    // imprimée. Elle permet de colorer juste / faux au moment de la frappe.
    bonnes: Record<string, Record<string, number>>;
  };
  items: ItemQcm[];
  copies: CopieQcm[];
};

export type ResumeQcm = {
  id: number;
  titre: string;
  classe: string;
  nom_fichier: string;
  versions: number;
  questions: number;
  copies: number;
  reponses_saisies: number;
  reponses_attendues: number;
  competences: number;
};

/** Scores et statistiques d'un QCM corrigé.
 *
 *  Le barème est `total_points` — le nombre de questions. Rien n'est ramené
 *  à 20 : le score brut est ce que le professeur veut lire.
 */
export type ResultatsQcm = {
  titre: string;
  classe: string;
  total_points: number;
  questions: { ref: string; label: string; poids: number }[];
  competences: { code: string; libelle: string }[];
  copies: {
    copie_id: number;
    prenom: string;
    nom: string;
    statut: "presente" | "absente" | "non_rendue";
    note_finale: number;
    questions_saisies: number;
    competences: { code: string; libelle: string; taux: number; niveau: string }[];
  }[];
  classe_stats: {
    effectif: number;
    absents: string[];
    moyenne: number;
    mediane: number;
    ecart_type: number;
    minimum: number;
    maximum: number;
    quartiles: number[];
    notes: number[];
    reussite_par_question: Record<string, number>;
    reussite_par_competence: Record<string, number>;
    /** Copies corrigées par question : le dénominateur des taux ci-dessus. */
    corrigees_par_question: Record<string, number>;
  };
};

/** Ce qu'un QCM sait de ses capacités et de ses compétences.
 *
 *  Une question dont `capacites` n'est pas vide arrive **déjà rattachée** de
 *  la banque : un nouvel appel ne la touchera pas.
 */
export type EtatCompetencesQcm = {
  titre: string;
  classe: string;
  niveau: string;
  fourchette: [number, number];
  questions: {
    ref: string;
    numero: number;
    enonce: string;
    reponses: number;
    capacites: string[];
  }[];
  competences: {
    code: string;
    libelle: string;
    refs: string[];
    capacites: { code: string; libelle: string }[];
  }[];
};

export type ReferentielQcm = {
  capacites_par_question: { ref: string; capacites: string[] }[];
  competences: {
    code: string;
    libelle: string;
    refs: string[];
    justification: string;
  }[];
};

export type PropositionQcm = {
  referentiel: ReferentielQcm;
  anomalies: Anomalie[];
  cout_dollars: number | null;
  origine: "appel" | "archive";
};

/** Une question de la banque. Elle vit indépendamment des QCM qui la servent :
 *  `question_qcm` pend au compte, pas à une classe. */
export type QuestionBanque = {
  id: number;
  identifiant: string;
  enonce: string;
  bloc: string | null;
  suite: string | null;
  /** Une figure accompagne l'énoncé. Le SVG se demande à part : un listing
   *  qui les porterait toutes ferait plusieurs mégaoctets. */
  a_une_figure: boolean;
  /** Le niveau où elle est née — un filtre, jamais un mur : un QCM de 1re spé
   *  pioche légitimement un automatisme de seconde. */
  niveau: string;
  creee_le: string;
  /** Sortie de la circulation : plus proposée à la composition, mais
   *  toujours là, et les QCM passés n'y voient rien. */
  retiree_le: string | null;
  /** Les classes qui ont déjà vu cette question — elles repartent avec leur
   *  QCM corrigé, donc la question sort de la salle. */
  classes_vues: string[];
  /** Taux de réussite cumulé, ou `null` si personne n'y a encore répondu :
   *  une question neuve n'a pas un taux de zéro, elle n'en a pas. */
  reussite: number | null;
  /** Des élèves y ont répondu : ses réponses ne peuvent plus être réécrites. */
  servie: boolean;
  qcm_servis: number;
  capacites: string[];
  reponses: { texte: string; exacte: boolean; erreur: string | null }[];
};

/** Une proposition de rattachement au programme, avant relecture.
 *
 *  Rien n'est écrit tant qu'elle n'est pas enregistrée : c'est la seule
 *  chose du système qu'on ne rejoue jamais. */
export type PropositionCapacites = {
  propose: { capacites_par_question: { ref: string; capacites: string[] }[] };
  anomalies: Anomalie[];
  cout_dollars: number | null;
  niveau: string;
  questions: {
    ref: string;
    enonce: string;
    capacites: string[];
    a_une_figure: boolean;
  }[];
};

/** Ce que la suppression d'un QCM emporterait. La banque n'y figure pas :
 *  elle pend au compte, pas au QCM, et lui survit. */
export type ImpactQcm = {
  qcm_id: number;
  titre: string;
  nom_fichier: string;
  classe: string;
  questions: number;
  competences: number;
  copies: number;
  reponses_saisies: number;
  documents: number;
  /** Quand un sujet a été produit pour la dernière fois — le seul indice
   *  qu'Atelier possède de ce qui a pu quitter le bureau. Produire un PDF
   *  n'est pas le distribuer : c'est au professeur de conclure. */
  dernier_sujet_le: string | null;
};

export type Anomalie = { gravite: "erreur" | "avertissement"; message: string };

export type Classe = {
  id: number;
  code: string;
  niveau: string;
  niveau_libelle: string;
  annee_scolaire: string;
  eleves: number;
  devoirs: number;
};

export type Eleve = { id: number; prenom: string; nom: string };

export type ImpactClasse = {
  classe_id: number;
  code: string;
  annee_scolaire: string;
  eleves: number;
  devoirs: { id: number; titre: string }[];
  notes_saisies: number;
  // Les QCM pendent à la classe comme les devoirs, et la cascade les emporte
  // pareillement. Les taire, c'était annoncer « 0 devoir » à qui s'apprête à
  // détruire un QCM corrigé.
  qcm: { id: number; titre: string }[];
  reponses_saisies: number;
};

export type Capacite = {
  code: string;
  theme: string;
  sous_theme: string | null;
  libelle: string;
};

export type Competence = {
  code: string;
  libelle: string;
  capacites: string[];
  justification: string;
};

export type Affectation = { ref: string; competences: string[] };

export type Referentiel = {
  competences: Competence[];
  affectations: Affectation[];
};

export type Proposition = {
  referentiel: Referentiel;
  anomalies: { gravite: "erreur" | "avertissement"; message: string }[];
  cout_dollars: number | null;
  origine: "appel" | "archive";
};

export type Messages = Record<string, Record<string, string>>;

export type JeuMessages = {
  messages: {
    code: string;
    non_acquis: string;
    en_cours: string;
    fragile: string;
    acquis: string;
  }[];
};

export type PropositionMessages = {
  jeu: JeuMessages;
  anomalies: { gravite: "erreur" | "avertissement"; message: string }[];
  cout_dollars: number | null;
};

export type BilanImport = {
  id: number;
  titre: string | null;
  format: string;
  exercices: number;
  questions: number;
  bonus: number;
  total_points: number;
};

export type ImpactDs = {
  ds_id: number;
  titre: string;
  exercices: number;
  questions: number;
  competences: number;
  copies: number;
  notes_saisies: number;
  feedbacks: number;
  documents: number;
};

export type Supprime = {
  fichier: string;
  type: "ds" | "classe" | "qcm";
  titre: string;
  classe: string;
  supprime_le: string;
  questions: number;
  notes: number;
  eleves: number;
  devoirs: number;
  octets: number;
};

export type Document = {
  id: number;
  type: string;
  chemin: string;
  copie_id: number | null;
  genere_le: string;
};

export const NIVEAUX = ["non_acquis", "en_cours", "fragile", "acquis"] as const;

export type Analyse = {
  synthese: string;
  groupes: {
    borne_basse: number;
    borne_haute: number;
    intitule: string;
    commentaire: string;
  }[];
  reussites: string[];
  fragilites: string[];
  pistes: string[];
};

export type Resume = {
  minimum: number;
  q1: number;
  mediane: number;
  q3: number;
  maximum: number;
  moyenne: number;
};

export type Indicateurs = {
  effectif: number;
  absents: string[];
  moyenne: number;
  mediane: number;
  ecart_type: number;
  minimum: number;
  maximum: number;
  quartiles: number[];
  notes: number[];
  reussite_par_question: Record<string, number>;
  reussite_par_competence: Record<string, number>;
  reussite_par_exercice: Record<string, number>;
  /** Copies corrigées par question : le dénominateur des taux ci-dessus. */
  corrigees_par_question: Record<string, number>;
  resume: Resume;
  resume_par_exercice: Record<string, Resume>;
};

export type EtatAnalyse = {
  ds: { titre: string; classe: string; total_points: number };
  indicateurs: Indicateurs;
  questions: { ref: string; label: string; exercice: number; bonus: boolean }[];
  competences: { code: string; libelle: string }[];
  analyse: Analyse | null;
  retouchee: boolean;
  genere_le: string | null;
};

export type Utilisateur = {
  id: number;
  identifiant: string;
  nom_affiche: string;
  email: string | null;
};

/** Levée quand le serveur exige une session : déclenche l'écran de connexion. */
export class SessionExpiree extends Error {}

async function appeler<T>(chemin: string, options?: RequestInit): Promise<T> {
  const reponse = await fetch(`/api${chemin}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!reponse.ok) {
    const detail = await reponse.json().catch(() => ({}));
    if (reponse.status === 401) throw new SessionExpiree("Connexion requise.");
    throw new Error(detail.detail ?? `HTTP ${reponse.status}`);
  }
  // 204 : pas de corps à lire.
  return reponse.status === 204 ? (undefined as T) : reponse.json();
}

export const api = {
  lireSession: () => appeler<Utilisateur>("/session"),

  ouvrirSession: (identifiant: string, motDePasse: string) =>
    appeler<Utilisateur>("/session", {
      method: "POST",
      body: JSON.stringify({ identifiant, mot_de_passe: motDePasse }),
    }),

  fermerSession: () => appeler<void>("/session", { method: "DELETE" }),

  importerDs: async (classeId: number, fichier: File, dateDevoir: string) => {
    const corps = new FormData();
    corps.append("classe_id", String(classeId));
    if (dateDevoir) corps.append("date_devoir", dateDevoir);
    corps.append("fichier", fichier);
    const reponse = await fetch("/api/ds", { method: "POST", body: corps });
    if (!reponse.ok) {
      const detail = await reponse.json().catch(() => ({}));
      throw new Error(detail.detail ?? `HTTP ${reponse.status}`);
    }
    return reponse.json() as Promise<BilanImport>;
  },

  supprimerDs: (dsId: number, forcer: boolean) =>
    appeler<ImpactDs & { sauvegarde: string }>(`/ds/${dsId}?forcer=${forcer}`, {
      method: "DELETE",
    }),

  impactDs: (dsId: number) => appeler<ImpactDs>(`/ds/${dsId}/impact-suppression`),

  lireCorbeille: () => appeler<Supprime[]>("/ds/corbeille/contenu"),

  restaurerDs: (fichier: string) =>
    appeler<{ id: number; titre: string }>(
      `/ds/corbeille/${encodeURIComponent(fichier)}/restaurer`,
      { method: "POST" },
    ),

  lireMessages: (dsId: number) =>
    appeler<Messages>(`/ds/${dsId}/competences/messages`),

  redigerMessages: (dsId: number) =>
    appeler<PropositionMessages>(`/ds/${dsId}/competences/messages/rediger`, {
      method: "POST",
    }),

  enregistrerMessages: (dsId: number, jeu: JeuMessages) =>
    appeler<{ enregistres: number; avertissements: string[] }>(
      `/ds/${dsId}/competences/messages`,
      { method: "PUT", body: JSON.stringify(jeu) },
    ),

  lireAppreciations: (dsId: number) =>
    appeler<Record<number, string>>(`/ds/${dsId}/appreciations`),

  enregistrerAppreciation: (copieId: number, texte: string) =>
    appeler<{ enregistree: boolean }>(`/ds/copies/${copieId}/appreciation`, {
      method: "PUT",
      body: JSON.stringify({ texte }),
    }),

  lireAnalyse: (dsId: number) => appeler<EtatAnalyse>(`/ds/${dsId}/analyse`),

  redigerAnalyse: (dsId: number) =>
    appeler<{
      analyse: Analyse;
      anomalies: { gravite: string; message: string }[];
      cout_dollars: number | null;
    }>(`/ds/${dsId}/analyse/rediger`, { method: "POST" }),

  modifierAnalyse: (dsId: number, analyse: Analyse) =>
    appeler<{ avertissements: string[] }>(`/ds/${dsId}/analyse`, {
      method: "PUT",
      body: JSON.stringify(analyse),
    }),

  produireAnalyse: (dsId: number) =>
    appeler<{ id: number; octets: number }>(`/ds/${dsId}/analyse/document`, {
      method: "POST",
    }),

  lireDocuments: (dsId: number) => appeler<Document[]>(`/ds/${dsId}/documents`),

  produireBilans: (dsId: number) =>
    appeler<{ id: number; nom: string; octets: number }>(
      `/ds/${dsId}/documents/feedback`,
      { method: "POST" },
    ),

  lienTelechargement: (documentId: number) =>
    `/api/ds/documents/${documentId}/fichier`,

  // Composé à la demande : le classeur suit la correction en cours.
  lienExportTableur: (dsId: number) => `/api/ds/${dsId}/export.xlsx`,

  // --- QCM ---------------------------------------------------------------

  listerQcm: () => appeler<ResumeQcm[]>("/qcm"),

  /** L'import rend les anomalies : une « erreur » signifie que rien n'a été
   *  écrit, un « avertissement » que quelque chose mérite un coup d'œil. */
  importerQcm: async (
    classeId: number,
    fichier: File,
    titre: string,
    date: string,
    versions: number,
  ) => {
    const corps = new FormData();
    corps.append("classe_id", String(classeId));
    corps.append("versions", String(versions));
    if (titre) corps.append("titre", titre);
    if (date) corps.append("date_passation", date);
    corps.append("fichier", fichier);

    const reponse = await fetch("/api/qcm", { method: "POST", body: corps });
    const detail = await reponse.json().catch(() => ({}));
    if (!reponse.ok) {
      if (reponse.status === 401) throw new SessionExpiree("Connexion requise.");
      // Le serveur range les anomalies dans le détail quand il refuse tout.
      const anomalies = detail.detail?.anomalies as Anomalie[] | undefined;
      if (anomalies) throw new Error(anomalies.map((a) => a.message).join(" "));
      throw new Error(detail.detail ?? `HTTP ${reponse.status}`);
    }
    return detail as { id: number; anomalies: Anomalie[] };
  },

  lireGrilleQcm: (qcmId: number) => appeler<GrilleQcm>(`/qcm/${qcmId}/grille`),

  /** La version corrige la lecture des réponses ; elle ne les détruit pas. */
  changerVersion: (copieId: number, version: string | null) =>
    appeler<{ version: string | null; relues: number }>(
      `/qcm/copies/${copieId}/version`,
      { method: "PUT", body: JSON.stringify({ version }) },
    ),

  /** Les places cochées, telles qu'elles ont été lues sur la copie. */
  enregistrerReponses: (
    copieId: number,
    reponses: Record<string, ReponseSaisie | null>,
  ) =>
    appeler<{ enregistrees: number }>(`/qcm/copies/${copieId}/reponses`, {
      method: "PUT",
      body: JSON.stringify({ reponses }),
    }),

  changerStatutQcm: (copieId: number, statut: CopieQcm["statut"]) =>
    appeler<{ statut: string }>(`/qcm/copies/${copieId}/statut`, {
      method: "PUT",
      body: JSON.stringify({ statut }),
    }),

  lireDocumentsQcm: (qcmId: number) =>
    appeler<Document[]>(`/qcm/${qcmId}/documents`),

  /** Les questions du QCM développées, au format du fichier d'origine —
   *  copie de travail pour rédiger un corrigé en regard. */
  lienQuestionsQcm: (qcmId: number) => `/api/qcm/${qcmId}/questions.typ`,

  lienDocumentQcm: (documentId: number) =>
    `/api/qcm/documents/${documentId}/fichier`,

  produireSujet: (qcmId: number, siegesParRangee?: number) =>
    appeler<{ id: number; octets: number }>(
      `/qcm/${qcmId}/sujet` +
        (siegesParRangee ? `?sieges_par_rangee=${siegesParRangee}` : ""),
      { method: "POST" },
    ),

  impactQcm: (qcmId: number) =>
    appeler<ImpactQcm>(`/qcm/${qcmId}/impact-suppression`),

  supprimerQcm: (qcmId: number, forcer: boolean) =>
    appeler<ImpactQcm & { sauvegarde: string }>(
      `/qcm/${qcmId}?forcer=${forcer}`,
      { method: "DELETE" },
    ),

  restaurerQcm: (fichier: string) =>
    appeler<{ id: number }>(
      `/qcm/corbeille/${encodeURIComponent(fichier)}/restaurer`,
      { method: "POST" },
    ),

  lireBanque: () => appeler<QuestionBanque[]>("/qcm/banque"),

  /** L'image d'une question figurée, telle que le sujet l'imprimera. */
  lienFigure: (identifiant: string) =>
    `/api/qcm/banque/${encodeURIComponent(identifiant)}/figure`,

  /** Retire une question de la circulation, ou l'y remet. N'efface rien. */
  retirerQuestion: (identifiant: string, retiree: boolean) =>
    appeler<{ retiree: boolean }>(
      `/qcm/banque/${encodeURIComponent(identifiant)}/retrait`,
      { method: "PUT", body: JSON.stringify({ retiree }) },
    ),

  /** N'efface qu'une question qui n'a jamais figuré dans un QCM. */
  supprimerQuestion: (identifiant: string) =>
    appeler<{ supprimee: string }>(
      `/qcm/banque/${encodeURIComponent(identifiant)}`,
      { method: "DELETE" },
    ),

  /** Compose un QCM depuis la banque. L'ordre des identifiants est celui du
   *  sujet imprimé : un QCM a une progression. */
  composerQcm: (composition: {
    classe_id: number;
    identifiants: string[];
    titre?: string;
    date_passation?: string;
    versions?: number;
  }) =>
    appeler<{ id: number; anomalies: Anomalie[] }>("/qcm/composer", {
      method: "POST",
      body: JSON.stringify(composition),
    }),

  /** Verse des questions à la banque, sans créer de QCM. Le niveau est
   *  déclaré : une question relève d'un niveau, pas d'une classe. */
  verserEnBanque: async (niveau: string, fichier: File) => {
    const corps = new FormData();
    corps.append("niveau", niveau);
    corps.append("fichier", fichier);

    const reponse = await fetch("/api/qcm/banque", { method: "POST", body: corps });
    const detail = await reponse.json().catch(() => ({}));
    if (!reponse.ok) {
      if (reponse.status === 401) throw new SessionExpiree("Connexion requise.");
      const anomalies = detail.detail?.anomalies as Anomalie[] | undefined;
      if (anomalies) throw new Error(anomalies.map((a) => a.message).join(" "));
      throw new Error(detail.detail ?? `HTTP ${reponse.status}`);
    }
    return detail as { versees: number; anomalies: Anomalie[] };
  },

  capacitesDuNiveau: (niveau: string) =>
    appeler<Capacite[]>(`/qcm/banque/capacites/${niveau}`),

  proposerCapacitesBanque: (niveau: string) =>
    appeler<PropositionCapacites>(
      `/qcm/banque/capacites/${niveau}/proposer`,
      { method: "POST" },
    ),

  enregistrerCapacitesBanque: (
    niveau: string,
    propose: PropositionCapacites["propose"],
  ) =>
    appeler<{ rattachements: number; avertissements: string[] }>(
      `/qcm/banque/capacites/${niveau}`,
      { method: "PUT", body: JSON.stringify(propose) },
    ),

  lireCompetencesQcm: (qcmId: number) =>
    appeler<EtatCompetencesQcm>(`/qcm/${qcmId}/competences`),

  capacitesQcm: (qcmId: number) =>
    appeler<Capacite[]>(`/qcm/${qcmId}/competences/capacites`),

  relirePropositionQcm: (qcmId: number) =>
    appeler<PropositionQcm>(`/qcm/${qcmId}/competences/proposition`),

  identifierCompetencesQcm: (qcmId: number) =>
    appeler<PropositionQcm>(`/qcm/${qcmId}/competences/identifier`, {
      method: "POST",
    }),

  enregistrerCompetencesQcm: (qcmId: number, referentiel: ReferentielQcm) =>
    appeler<{ competences: number; capacites: number; avertissements: string[] }>(
      `/qcm/${qcmId}/competences`,
      { method: "PUT", body: JSON.stringify(referentiel) },
    ),

  lireMessagesQcm: (qcmId: number) =>
    appeler<Messages>(`/qcm/${qcmId}/competences/messages`),

  /** Les effectifs orientent la rédaction, ils n'entrent jamais dans le
   *  texte : le serveur refuse un message qui parlerait de la classe. */
  redigerMessagesQcm: (qcmId: number) =>
    appeler<PropositionMessages & { corrigees: number }>(
      `/qcm/${qcmId}/competences/messages/rediger`,
      { method: "POST" },
    ),

  enregistrerMessagesQcm: (qcmId: number, jeu: JeuMessages) =>
    appeler<{ enregistres: number; avertissements: string[] }>(
      `/qcm/${qcmId}/competences/messages`,
      { method: "PUT", body: JSON.stringify(jeu) },
    ),

  lireResultatsQcm: (qcmId: number) =>
    appeler<ResultatsQcm>(`/qcm/${qcmId}/resultats`),

  lireAppreciationsQcm: (qcmId: number) =>
    appeler<Record<number, string>>(`/qcm/${qcmId}/appreciations`),

  enregistrerAppreciationQcm: (copieId: number, texte: string) =>
    appeler<{ enregistree: boolean }>(`/qcm/copies/${copieId}/appreciation`, {
      method: "PUT",
      body: JSON.stringify({ texte }),
    }),

  produireBilansQcm: (qcmId: number) =>
    appeler<{ id: number; octets: number }>(`/qcm/${qcmId}/documents/bilans`, {
      method: "POST",
    }),

  produireCorrigeQcm: (qcmId: number) =>
    appeler<{ id: number; octets: number }>(`/qcm/${qcmId}/documents/corrige`, {
      method: "POST",
    }),

  capacites: (dsId: number) =>
    appeler<Capacite[]>(`/ds/${dsId}/competences/capacites`),

  relireProposition: (dsId: number) =>
    appeler<Proposition>(`/ds/${dsId}/competences/proposition`),

  identifierCompetences: (dsId: number) =>
    appeler<Proposition>(`/ds/${dsId}/competences/identifier`, { method: "POST" }),

  enregistrerCompetences: (dsId: number, referentiel: Referentiel) =>
    appeler<{ competences: number; liens: number; avertissements: string[] }>(
      `/ds/${dsId}/competences`,
      { method: "PUT", body: JSON.stringify(referentiel) },
    ),

  listerClasses: () => appeler<Classe[]>("/classes"),

  creerClasse: (classe: { code: string; niveau: string; annee_scolaire: string }) =>
    appeler<Classe>("/classes", { method: "POST", body: JSON.stringify(classe) }),

  lireEleves: (classeId: number) => appeler<Eleve[]>(`/classes/${classeId}/eleves`),

  importerEleves: async (classeId: number, fichier: File) => {
    // multipart : ne pas fixer Content-Type, le navigateur pose la frontière.
    const corps = new FormData();
    corps.append("fichier", fichier);
    const reponse = await fetch(`/api/classes/${classeId}/eleves`, {
      method: "POST",
      body: corps,
    });
    if (!reponse.ok) {
      const detail = await reponse.json().catch(() => ({}));
      throw new Error(detail.detail ?? `HTTP ${reponse.status}`);
    }
    return reponse.json() as Promise<{
      lus: number;
      inscrits: number;
      deja_presents: number;
      effectif: number;
    }>;
  },

  desinscrire: async (classeId: number, eleveId: number) => {
    const reponse = await fetch(`/api/classes/${classeId}/eleves/${eleveId}`, {
      method: "DELETE",
    });
    if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
  },

  impactClasse: (classeId: number) =>
    appeler<ImpactClasse>(`/classes/${classeId}/impact-suppression`),

  supprimerClasse: (classeId: number, forcer: boolean) =>
    appeler<ImpactClasse & { sauvegarde: string }>(
      `/classes/${classeId}?forcer=${forcer}`,
      { method: "DELETE" },
    ),

  restaurerClasse: (fichier: string) =>
    appeler<Classe>(`/classes/corbeille/${encodeURIComponent(fichier)}/restaurer`, {
      method: "POST",
    }),

  listerDs: () => appeler<ResumeDs[]>("/ds"),
  lireGrille: (dsId: number) => appeler<Grille>(`/ds/${dsId}/grille`),

  lireDs: (dsId: number) =>
    appeler<{ competences: { code: string; libelle: string; capacites: string[] }[] }>(
      `/ds/${dsId}`,
    ),

  lireResultats: (dsId: number) =>
    appeler<{
      copies: {
        copie_id: number;
        prenom: string;
        nom: string;
        statut: string;
        note_finale: number;
        competences: { code: string; libelle: string; taux: number; niveau: string }[];
      }[];
    }>(`/ds/${dsId}/resultats`),

  enregistrerNotes: (copieId: number, notes: Record<string, number | null>) =>
    appeler<{ enregistrees: number }>(`/ds/copies/${copieId}/notes`, {
      method: "PUT",
      body: JSON.stringify({ notes }),
    }),

  modifierBareme: (dsId: number, poids: Record<string, number>) =>
    appeler<{ total_points: number; total_bonus: number }>(`/ds/${dsId}/bareme`, {
      method: "PUT",
      body: JSON.stringify({ poids }),
    }),

  changerStatut: (copieId: number, statut: Copie["statut"]) =>
    appeler<{ statut: string }>(`/ds/copies/${copieId}/statut`, {
      method: "PUT",
      body: JSON.stringify({ statut }),
    }),
};
